import styled, { css } from 'styled-components'

import { TextPreviewItem as LMFTextPreviewItem, fromLg, fromMd } from '@syconium/little-miss-figgy'

import { useEmbroidery } from '../../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { TextFormValues } from '../../../../../containers/EmbroideryContainer/types'
import { useEmbroideryDesignsQuery } from '../../../../../containers/EmbroideryContainer/useEmbroideryDesigns'
import { getFlatlayImageOffset } from '../../../../../containers/EmbroideryContainer/utils/getFlatlayImageOffset'
import {
  isLightProductColor,
  isSufficientEmbroideryColorContrast,
} from '../../../../../containers/EmbroideryContainer/utils/unorganized'
import { toEmbroideryFontFamily } from '../../../../../lib/embroideryFonts'
import { EMBROIDERY_TEXT_FILTER_ID } from '../../../Modal/primitives/EmbroideryThreadFilter'
import { usePreviewFlatlayImage } from '../../EmbroideryModalPreview/PreviewFlatlayImageContext'

type PreviewFontStyle = 'block' | 'serif' | 'script'

export const TextPreviewItem = <T extends Omit<TextFormValues, '__type'>>({
  values,
}: {
  values: T
}) => {
  const { productColorHex, productFlatlayImage } = useEmbroidery()
  const previewFlatlayImage = usePreviewFlatlayImage()
  const { data: embroideryDesigns } = useEmbroideryDesignsQuery()
  const fontScale =
    embroideryDesigns?.embroideryOptions.fonts.find(font => font.key === values.font?.key)?.scale ??
    1

  const isSufficientColorContrast = values.textColor
    ? isSufficientEmbroideryColorContrast(productColorHex, values.textColor.hexCode)
    : undefined

  // Detecting the color of the ghost image, not the productHex.
  // The default is graphite - a dark color
  // So we short circuit the isLightProduct logic if using the default
  const activeFlatlay = previewFlatlayImage ?? productFlatlayImage
  const isDefaultFlatlay = activeFlatlay?.image.url === productFlatlayImage?.image.url
  const isLightProduct = !isDefaultFlatlay && isLightProductColor(productColorHex)

  const backgroundImageOffsets = getFlatlayImageOffset({
    flatlayImage: previewFlatlayImage ?? productFlatlayImage,
    placement: values.textPlacement,
  })

  const xOffset: string | undefined = backgroundImageOffsets?.x
    ? `${backgroundImageOffsets.x * 100}%`
    : undefined
  const yOffset: string | undefined = backgroundImageOffsets?.y
    ? `${backgroundImageOffsets.y * 100}%`
    : undefined
  const rotation: number | undefined = backgroundImageOffsets?.rotation
  const placementScale: number | undefined = backgroundImageOffsets?.scale ?? 1
  const scale = fontScale * placementScale

  if (!values.textPlacement || !backgroundImageOffsets) return null

  const fontStyle = normalizeFontStyle(values.font?.key)

  return (
    <>
      <StyledTextPreviewItem
        color={values.textColor?.hexCode ?? 'inherit'}
        firstLine={values.firstLine}
        fontSizeRatio={values.font?.scale ?? 1}
        fontStyle={toEmbroideryFontFamily(values.font?.designFile) ?? 'inherit'}
        rotation={rotation}
        scale={scale}
        secondLine={values.secondLine}
        xOffset={xOffset}
        yOffset={yOffset}
        warning={
          Boolean(values.firstLine || values.secondLine) && !isSufficientColorContrast
            ? isLightProduct
              ? 'light'
              : 'dark'
            : undefined
        }
        aria-hidden={true}
        $fontStyle={fontStyle}
        $textLength={Math.max(values.firstLine.length, values.secondLine?.length ?? 0)}
      />
    </>
  )
}

const StyledTextPreviewItem = styled(LMFTextPreviewItem)<{
  $fontStyle: PreviewFontStyle
  $textLength: number
}>`
  filter: url(#${EMBROIDERY_TEXT_FILTER_ID});
  ${({ $fontStyle, $textLength }) => getPreviewFontSizeCss($fontStyle, $textLength)}
`

const LONG_TEXT_THRESHOLD = 23
const MEDIUM_TEXT_THRESHOLD = 15

const normalizeFontStyle = (value?: string): PreviewFontStyle => {
  const normalized = value?.toLowerCase()

  if (normalized === 'script' || normalized === 'serif') {
    return normalized
  }

  return 'block'
}

const getTextLengthBucket = (textLength: number) => {
  if (textLength >= LONG_TEXT_THRESHOLD) return 'long'
  if (textLength >= MEDIUM_TEXT_THRESHOLD) return 'medium'
  return 'short'
}

// TODO: This is messy and we could really probably solve this using the scaling from
// the offset data, but for sake of time, I'm pushing this for now and we can come back
// when time permits.
const getPreviewFontSizeCss = (fontStyle: PreviewFontStyle, textLength: number) => {
  const lengthBucket = getTextLengthBucket(textLength)

  const fontSizes = {
    block: {
      short: {
        sm: 'clamp(10px, 2.3vw, 12px)',
        md: 'clamp(12px, 1.3vw, 14px)',
        lg: 'clamp(14px, 1vw, 16px)',
      },
      medium: {
        sm: 'clamp(9px, 2.1vw, 11px)',
        md: 'clamp(11px, 1.1vw, 13px)',
        lg: 'clamp(13px, 0.9vw, 15px)',
      },
      long: {
        sm: 'clamp(8px, 1.7vw, 10px)',
        md: 'clamp(10px, 0.9vw, 11px)',
        lg: 'clamp(12px, 0.8vw, 13px)',
      },
    },
    serif: {
      short: {
        sm: 'clamp(9px, 2.1vw, 11px)',
        md: 'clamp(11px, 1.2vw, 13px)',
        lg: 'clamp(13px, 1vw, 15px)',
      },
      medium: {
        sm: 'clamp(8px, 1.9vw, 10px)',
        md: 'clamp(10px, 1vw, 12px)',
        lg: 'clamp(12px, 0.9vw, 14px)',
      },
      long: {
        sm: 'clamp(8px, 1.4vw, 9px)',
        md: 'clamp(9px, 0.8vw, 10px)',
        lg: 'clamp(11px, 0.7vw, 13px)',
      },
    },
    script: {
      short: {
        sm: 'clamp(8px, 2vw, 10px)',
        md: 'clamp(10px, 1vw, 12px)',
        lg: 'clamp(12px, 0.9vw, 14px)',
      },
      medium: {
        sm: 'clamp(8px, 1.5vw, 9px)',
        md: 'clamp(9px, 0.9vw, 11px)',
        lg: 'clamp(11px, 0.8vw, 13px)',
      },
      long: {
        sm: 'clamp(7px, 1.3vw, 8px)',
        md: 'clamp(8px, 0.8vw, 9px)',
        lg: 'clamp(10px, 0.6vw, 11px)',
      },
    },
  } as const

  const sizes = fontSizes[fontStyle][lengthBucket]

  return css`
    && {
      font-size: ${sizes.sm};

      ${fromMd} {
        font-size: ${sizes.md};
      }

      ${fromLg} {
        font-size: ${sizes.lg};
      }
    }
  `
}
